import { Heading } from '@chakra-ui/react';

interface ModuleLabelProps {
  /**
   * The card's single label. Rendered as the card's heading in normal title
   * case; CSS uppercases it (screen readers get the title-case text).
   */
  label: string;
}

/** Brand gradient of the accent bar (decorative: a pseudo-element, not in the a11y tree). */
const ACCENT_GRADIENT = 'linear-gradient(180deg, var(--color-primary-purple), var(--color-tertiary-teal))';

/**
 * The header of every restaurant-profile card: ONE label with the eyebrow's
 * distinctive character (founder: "I like the styling of the eyebrow", final
 * after review): UPPERCASE, wide 0.1em tracking, bold 14px (a step on the
 * app's type scale), `--card-text` (17.4:1 on the white card, high on the dark
 * card), and a short purple-to-teal rounded bar (4 x 14px) before the text.
 *
 * It is a real heading element (`h2`, so the page's outline is the h1 name plus
 * a list of card h2s) and the visible text is its accessible name. The bar is a
 * CSS pseudo-element, never read.
 *
 * Only the restaurant-profile cards use this component; the Dashboard's bento
 * shell has its own label markup (ModuleFrame) and is unaffected.
 *
 * Renders as the first element inside a card's `--card-*` token surface.
 */
export function ModuleLabel({ label }: ModuleLabelProps) {
  return (
    <Heading
      as="h2"
      display="flex"
      alignItems="center"
      gap="8px"
      mb={3}
      fontSize="sm"
      fontWeight="bold"
      letterSpacing="0.1em"
      textTransform="uppercase"
      lineHeight={1.25}
      color="var(--card-text)"
      sx={{
        '&::before': {
          content: '""',
          flexShrink: 0,
          width: '4px',
          height: '14px',
          borderRadius: '999px',
          background: ACCENT_GRADIENT,
        },
      }}
    >
      {label}
    </Heading>
  );
}

export default ModuleLabel;
